import * as React from 'react'
import { TooltipContentProps } from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'

import { BucketType } from '@/schema'
import { formatDisplayDate } from '@/utils'
import { DATE_FORMAT } from '@/utils/constants'

import { ChartDataKey, MONTH_FORMAT } from '../../constants'
import { useDashboardStore } from '../../store'

type TChartTooltipProps = TooltipContentProps<ValueType, NameType> & {
  chartLabel: string
}

export const ChartTooltip: React.FC<TChartTooltipProps> = ({
  active,
  payload,
  label,
  chartLabel,
}) => {
  const { aggregationPeriod } = useDashboardStore()

  const point = React.useMemo(() => payload?.[0]?.payload, [payload])
  const value = React.useMemo(() => payload?.[0]?.value, [payload])

  // Determine the value and unit based on the data point type
  const displayValue = React.useMemo(() => {
    if (!point) return null
    if (value !== undefined) return value

    if (ChartDataKey.REVIEW_TIME_TREND in point)
      return point.avg_cycle_time_days
    if (ChartDataKey.RESOLUTION_RATE_TREND in point) {
      return point.avg_resolution_rate_percentage
    }
    return null
  }, [point, value])

  const unit = React.useMemo(() => {
    if (!point) return ''
    if (ChartDataKey.REVIEW_TIME_TREND in point) return 'days'
    if (ChartDataKey.RESOLUTION_RATE_TREND in point) return '%'
    return ''
  }, [point])

  const formattedValue = React.useMemo(() => {
    if (displayValue === null || displayValue === undefined) return 'N/A'
    if (!point) return 'N/A'

    if (ChartDataKey.RESOLUTION_RATE_TREND in point) return `${displayValue}%`
    return `${displayValue} ${unit}`
  }, [displayValue, point, unit])

  const dateFormatter = React.useMemo(() => {
    return aggregationPeriod === BucketType.week ? DATE_FORMAT : MONTH_FORMAT
  }, [aggregationPeriod])

  if (!active || !payload || payload.length === 0) return null
  if (!point) return null

  return (
    <div className="rounded-md bg-primary-solid px-lg py-md shadow-lg text-xs text-primary-on-brand">
      <div className="font-semibold mb-1">
        {label ? formatDisplayDate(label as string, dateFormatter) : '-'}
      </div>
      {displayValue !== null && (
        <div className="">
          {chartLabel && <span className="mr-1">{chartLabel}:</span>}
          <span className="font-medium">{formattedValue}</span>
        </div>
      )}
    </div>
  )
}
